
import { PropType } from 'vue'
import { AxiosResponse } from 'axios'
import { createValidations } from '@/helpers/commons/validationHelper'
import { defineComponent } from 'vue'
import { IKnCalculatedField, IKnCalculatedFieldFunction } from '@/components/functionalities/KnCalculatedField/KnCalculatedField'
import { VCodeMirror } from 'vue3-code-mirror'

import Dropdown from 'primevue/dropdown'
import Dialog from 'primevue/dialog'
import KnHint from '@/components/UI/KnHint.vue'
import Message from 'primevue/message'
import ScrollPanel from 'primevue/scrollpanel'
import useValidate from '@vuelidate/core'

export default defineComponent({
    name: 'calculated-field',
    components: { Dialog, Dropdown, KnHint, Message, ScrollPanel, VCodeMirror },
    props: {
        fields: Array,
        visibility: Boolean,
        readOnly: Boolean,
        descriptor: Object,
        template: {} as any,
        valid: Boolean,
        source: String,
        propCalcFieldFunctions: { type: Array as PropType<IKnCalculatedFieldFunction[]>, required: true }
    },
    data() {
        return {
            cf: { formula: '' } as IKnCalculatedField,
            allCategories: { name: 'ALL', code: 'ALL' },
            selectedFunction: {},
            selectedCategory: '',
            availableCategories: [] as any,
            codeMirror: null as any,
            availableFunctions: [] as any,
            scriptOptions: {
                mode: 'text/x-mathematica',
                indentWithTabs: true,
                smartIndent: true,
                lineWrapping: true,
                matchBrackets: true,
                autofocus: true,
                theme: 'eclipse',
                lineNumbers: true,
                readOnly: this.readOnly
            },
            v$: useValidate() as any,
            formulaValidationInterval: {} as any,
            isValidFormula: false,
            calcFieldFunctions: [] as IKnCalculatedFieldFunction[],
            showHelpPanel: false
        }
    },
    emits: ['save', 'cancel', 'update:readOnly'],
    created() {
        this.calcFieldFunctions = [...this.propCalcFieldFunctions]
        this.availableFunctions = [...this.calcFieldFunctions].sort((a, b) => {
            return a.name.localeCompare(b.name)
        })
        this.availableFunctions.forEach((x) => {
            x.category = x.category.toUpperCase()
        })

        this.cf = { formula: '' } as IKnCalculatedField

        if (!this.readOnly && this.template && !this.template.parameters && this.source === 'QBE') {
            this.cf = { colName: this.template.alias, formula: this.template.expression } as IKnCalculatedField
        }
        this.handleCategories()
    },

    updated() {
        if (!this.cf.formula) this.cf.formula = ''

        if (this.readOnly && this.template && this.template.parameters) {
            this.cf = {} as IKnCalculatedField
            for (var i = 0; i < this.template.parameters.length; i++) {
                if (this.template.parameters[i]['name'] == 'formula') this.cf.formula = this.template.parameters[i]['value']
                else if (this.template.parameters[i]['name'] == 'colName') this.cf.colName = this.template.parameters[i]['value']
            }
        }

        if (!this.readOnly && this.template && !this.template.parameters && this.source === 'QBE') {
            this.cf = { colName: this.template.alias, formula: this.template.expression } as IKnCalculatedField
        }
    },

    validations() {
        if (this.descriptor) {
            return { cf: createValidations('cf', this.descriptor.validations) }
        }
        return {}
    },

    methods: {
        handleClick(af) {
            if (JSON.stringify(this.selectedFunction) === JSON.stringify(af)) {
                this.selectedFunction = {}
            } else {
                this.selectedFunction = af
            }
        },
        apply(): void {
            this.$emit('save', this.cf)
            this.clearForm()
        },
        cancel(): void {
            this.$emit('update:readOnly', false)
            this.$emit('cancel', this.cf)
            this.clearForm()
        },
        clearForm(): void {
            this.cf = { formula: '' } as IKnCalculatedField
            this.selectedFunction = {}
            this.selectedCategory = ''
        },
        filterFunctions() {
            let tmp = [...this.calcFieldFunctions].sort((a, b) => {
                return a.name.localeCompare(b.name)
            })
            tmp.forEach((x) => {
                x.category = x.category.toUpperCase()
            })
            this.availableFunctions = tmp
            if (this.selectedCategory && this.selectedCategory !== this.allCategories.name) {
                let cat = this.selectedCategory as any
                this.availableFunctions = tmp.filter((x) => x.category.toUpperCase() === cat.toUpperCase())
            }
        },
        handleDragover(ev) {
            if (this.readOnly) return
            const doc = this.$refs.formula as any
            var cursor = doc.editor.getCursor()
            if (ev.target.className.includes('field-')) {
                doc.editor.markText(0, cursor)
            }
        },
        handleCategories() {
            let tmp = [] as any

            this.calcFieldFunctions
                .sort((a, b) => {
                    return a.name.localeCompare(b.name)
                })
                .map((x) => ({ name: x.category, code: x.category.toUpperCase() }))
                .forEach((element) => {
                    if (tmp.filter((y) => y.code === element.code).length == 0) tmp.push({ name: element.name, code: element.code })
                })

            if (tmp.filter((x) => x.name === this.allCategories.name).length == 0) tmp = [this.allCategories, ...tmp]

            this.availableCategories = tmp
        },
        allowDrop(ev) {
            ev.preventDefault()
        },
        clearCodemirror(editor, cursor, data) {
            if (editor.somethingSelected()) {
                let selections = editor.getSelections()
                for (var sel of selections) {
                    editor.replaceRange('', { line: cursor.line, ch: cursor.ch - JSON.stringify(data).length }, { line: cursor.line, ch: cursor.ch - JSON.stringify(data).length + sel.length })
                }
            }
        },

        dragElement(ev, item, elementType: String) {
            if (this.readOnly) return
            if (elementType === 'function') {
                ev.dataTransfer.setData('text/plain', JSON.stringify({ item: item.formula, elementType: elementType }))
            } else if (elementType === 'field') {
                ev.dataTransfer.setData('text/plain', JSON.stringify({ item: item, elementType: elementType }))
            }
            ev.dataTransfer.effectAllowed = 'copy'
        },

        drop(ev) {
            if (this.readOnly) return
            ev.stopPropagation()
            ev.preventDefault()

            var data = JSON.parse(ev.dataTransfer.getData('text/plain'))

            const doc = this.$refs.formula as any
            let editor = doc.editor
            var cursor = editor.getCursor()

            this.clearCodemirror(editor, cursor, data)

            editor.clearHistory()

            cursor = editor.getCursor()

            let start = editor.findWordAt(cursor).anchor.ch
            let end = editor.findWordAt(cursor).head.ch

            let from = { line: cursor.line, ch: start }
            let to = { line: cursor.line, ch: end }

            let range = editor.getRange(from, to)
            let fieldAlias = this.source !== 'QBE' ? '$F{' + data.item.fieldAlias + '}' : data.item.fieldAlias
            let spContent = data.elementType === 'function' ? data.item : fieldAlias

            if (range === '' || range.match(/\(|\)|,|\./g)) {
                editor.replaceSelection(spContent, cursor)
            } else {
                const sp = document.createElement('span')
                sp.textContent = spContent
                editor.doc.markText(from, to, {
                    replacedWith: sp,
                    inclusiveLeft: false,
                    inclusiveRight: false
                })
            }

            let lines = document.querySelector('.CodeMirror-lines')
            if (lines) {
                let textEl = lines.querySelector('div span') as any

                if (textEl) this.cf.formula = textEl.innerText
            }
        },
        applyValidationResultsToFormula() {
            const doc = this.$refs.formula as any
            let editor = doc.editor

            let from = { line: editor.firstLine(), ch: 0 }
            let to = { line: editor.lastLine(), ch: editor.getLine(editor.lastLine()).length }

            if (!this.isValidFormula) {
                editor.markText(from, to, { className: 'syntax-error' })
            } else {
                editor.markText(from, to, { className: 'no-syntax-error' })
            }
        }
    },
    watch: {
        selectedFunction(newValue, oldValue) {
            if (newValue && oldValue !== newValue && newValue.label) {
                this.showHelpPanel = true
            } else {
                this.showHelpPanel = false
            }
        },
        readOnly(value) {
            this.scriptOptions.readOnly = value
        },
        visibility(newV, oldV) {
            if (newV && newV !== oldV) {
                if (!this.selectedCategory) {
                    if (this.descriptor?.defaultSelectedCategory) this.selectedCategory = this.descriptor?.defaultSelectedCategory
                    else this.selectedCategory = this.allCategories.name
                }
            }
        },
        cf: {
            handler() {
                if (this.cf.formula) {
                    if (this.descriptor?.validationServiceUrl) {
                        this.formulaValidationInterval = setInterval(() => {
                            this.$http.get(this.descriptor?.validationServiceUrl).then((response: AxiosResponse<any>) => {
                                this.isValidFormula = response.data[0]
                                this.applyValidationResultsToFormula()
                            })
                            clearInterval(this.formulaValidationInterval)
                            this.formulaValidationInterval = null
                        }, 2500)
                    } else {
                        this.isValidFormula = true
                    }
                }
            },
            deep: true
        }
    },
    computed: {
        saveButtonDisabled(): any {
            if (typeof this.valid === 'undefined') return this.v$.$invalid || !this.isValidFormula
            else return this.v$.$invalid || !this.isValidFormula || !this.valid
        }
    }
})
